
export interface DateRange {
  startDate: string; 
  endDate: string;   
}

export interface DayOfWeekFilter {
  selectedDays: number[]; 
  allDays: boolean; 
}

export interface TimeFilter {
  selectedHours: number[]; 
  allHours: boolean; 
  timePeriods: {
    morning?: boolean;    
    afternoon?: boolean;  
    evening?: boolean;    
    lateNight?: boolean; 
    [key: string]: boolean | undefined; 
  };
}

export interface DiningBehaviorFilter {
  selectedBehaviors: string[]; 
  allBehaviors: boolean;
}

export interface MenuCategoryFilter {
  selectedCategories: string[]; 
  allCategories: boolean;
}

export interface FilterState {
  dateRange: DateRange;
  dayOfWeek: DayOfWeekFilter;
  time: TimeFilter;
  diningBehavior: DiningBehaviorFilter;
}

export interface FilterActions {
  setDateRange: (dateRange: DateRange) => void;
  setDayOfWeekFilter: (filter: DayOfWeekFilter) => void;
  setTimeFilter: (filter: TimeFilter) => void;
  setDiningBehaviorFilter: (filter: DiningBehaviorFilter) => void;
  resetFilters: () => void;
}


export const DAY_NAMES = ['일', '월', '화', '수', '목', '금', '토'];

export const TIME_PERIODS = {
  morning: { label: '아침 (6AM-12PM)', hours: [6, 7, 8, 9, 10, 11] },
  afternoon: { label: '오후 (12PM-5PM)', hours: [12, 13, 14, 15, 16] },
  evening: { label: '저녁 (5PM-10PM)', hours: [17, 18, 19, 20, 21] },
  lateNight: { label: '심야 (10PM-6AM)', hours: [22, 23, 0, 1, 2, 3, 4, 5] }
};

export const DINING_GROUPS = [
  { 
    value: 'In-Store Order', 
    label: 'In-Store',
    icon: '🏪',
    behaviors: ['DINE_IN', 'TAKE_OUT'] 
  },
  { 
    value: 'Digital Order', 
    label: 'Digital',
    icon: '📱',
    behaviors: ['DELIVERY', 'TAKE_OUT'] 
  },
  { 
    value: 'Other', 
    label: 'Other',
    icon: '❓',
    behaviors: ['UNKNOWN'] 
  }
];

export const DINING_BEHAVIORS = [
  { value: 'DINE_IN', label: 'Dine In', icon: '🏪' },
  { value: 'TAKE_OUT', label: 'Take Out', icon: '🥡' },
  { value: 'DELIVERY', label: 'Delivery', icon: '🚚' },
  { value: 'UNKNOWN', label: 'Unknown', icon: '❓' }
];


export const FALLBACK_FILTER_STATE: FilterState = {
  dateRange: {
    
    startDate: (() => {
      const today = new Date();
      const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
      return `${firstDay.getFullYear()}-${String(firstDay.getMonth() + 1).padStart(2, '0')}-${String(firstDay.getDate()).padStart(2, '0')}`;
    })(),
    endDate: (() => {
      const today = new Date();
      return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    })()
  },
  dayOfWeek: {
    selectedDays: [0, 1, 2, 3, 4, 5, 6], 
    allDays: true
  },
  time: {
    selectedHours: Array.from({ length: 24 }, (_, i) => i), 
    allHours: true,
    timePeriods: {
      morning: true,    
      afternoon: true,
      evening: true,
      lateNight: true
    }
  },
  diningBehavior: {
    selectedBehaviors: ['DINE_IN', 'TAKE_OUT', 'DELIVERY'], 
    allBehaviors: true
  }
};


export const createDefaultFilterState = (
  dateData?: {
    defaultDateRange: { startDate: string; endDate: string };
  },
  operatingHours?: number[]
): FilterState => {
  
  const availableHours = operatingHours || Array.from({ length: 24 }, (_, i) => i);
  
  return {
    dateRange: dateData?.defaultDateRange || FALLBACK_FILTER_STATE.dateRange,
    dayOfWeek: { ...FALLBACK_FILTER_STATE.dayOfWeek },
    time: {
      selectedHours: [...availableHours], 
      allHours: true,
      timePeriods: {
        morning: true,
        afternoon: true,
        evening: true,
        lateNight: true
      }
    },
    diningBehavior: { ...FALLBACK_FILTER_STATE.diningBehavior }
  };
};


export const DEFAULT_FILTER_STATE = FALLBACK_FILTER_STATE;